import { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";

const callCalendarLinkAPI = async (structuredEventData, apiBaseUrl, setCalendarLink) => {
  try {
    // Use data from structured_event API response
    const eventData = structuredEventData.data || structuredEventData;

    const response = await fetch(`${apiBaseUrl}/calendar_link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventData.event_name,
        start_datetime: eventData.start_datetime,
        end_datetime: eventData.end_datetime,
        location: eventData.location,
        details: eventData.details || '',
        timezone: eventData.timezone
      }),
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Calendar link created:', result);
      
      // Set the calendar link from the API response
      if (result.calendar_link || result['link'] || result.url) {
        setCalendarLink(result.calendar_link || result['link'] || result.url);
      }
    } else {
      console.error('Calendar link API failed:', response.statusText);
    }
  } catch (error) {
    console.error('Error calling calendar link API:', error);
  }
};

const ActivityPage = () => {
  const { activityId } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarLink, setCalendarLink] = useState(null);
  const [structuredEventData, setStructuredEventData] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (!apiBaseUrl) {
          setError('API URL not configured');
          setLoading(false);
          return;
        }

        const response = await fetch(`${apiBaseUrl}/activities/${activityId}`);
        if (response.ok) {
          const data = await response.json();
          // Handle nested response structure: {"activity": {...}}
          const activityData = data.activity || data;
          setActivity(activityData);
          
          // Call structured_event API with activity description
          if (activityData && activityData.description) {
            try {
              const structuredResponse = await fetch(`${apiBaseUrl}/structured_event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  description: activityData.description.replace(/<[^>]*>/g, '') // Strip HTML tags
                }),
              });

              if (structuredResponse.ok) {
                const structuredData = await structuredResponse.json();
                console.log('Structured event data:', structuredData);
                
                // Store structured event data
                setStructuredEventData(structuredData);
                
                // Call calendar link API with structured data
                await callCalendarLinkAPI(structuredData, apiBaseUrl, setCalendarLink);
              } else {
                console.error('Structured event API failed:', structuredResponse.statusText);
              }
            } catch (structuredError) {
              console.error('Error calling structured event API:', structuredError);
            }
          }
        } else {
          setError('Activity not found');
        }
      } catch (err) {
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    if (activityId) {
      fetchActivity();
    }
  }, [activityId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg">Loading activity...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <RouterLink 
            to="/" 
            className="eventally-button py-2 px-4 rounded-lg font-medium text-white"
          >
            Back to Home
          </RouterLink>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg mb-4">Activity not found</div>
          <RouterLink 
            to="/" 
            className="eventally-button py-2 px-4 rounded-lg font-medium text-white"
          >
            Back to Home
          </RouterLink>
        </div>
      </div>
    );
  }

  // Use structured event datetime or fallback to original activity date
  let formattedDate = 'Date not available';
  
  if (structuredEventData && structuredEventData.data && structuredEventData.data.start_datetime) {
    // Parse structured datetime format: "20250908T191500" 
    const dateTimeStr = structuredEventData.data.start_datetime;
    const year = dateTimeStr.slice(0, 4);
    const month = dateTimeStr.slice(4, 6);
    const day = dateTimeStr.slice(6, 8);
    const hour = dateTimeStr.slice(9, 11);
    const minute = dateTimeStr.slice(11, 13);
    
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
    formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else if (activity.date) {
    // Fallback to original activity date
    const isoDateString = activity.date.includes(' ') ? activity.date.replace(' ', 'T') : activity.date;
    const date = new Date(isoDateString);
    
    formattedDate = isNaN(date.getTime()) 
      ? 'Invalid date'
      : date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="eventally-gradient-bg px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <RouterLink 
            to="/" 
            className="inline-flex items-center mb-4 px-3 py-1 rounded-lg bg-white hover:shadow-lg transition-all"
            style={{color: 'var(--eventally-dark)'}}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
            Back to EventAlly
          </RouterLink>
          <h1 className="eventally-brand text-3xl font-bold" style={{color: 'var(--eventally-dark)'}}>
            Activity Details
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Date */}
          <div className="mb-6">
            {calendarLink ? (
              <a 
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg hover:shadow-lg transition-all cursor-pointer"
                style={{backgroundColor: 'var(--eventally-gray)'}}
                title="Add to calendar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" style={{color: 'var(--eventally-coral)'}}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className="font-semibold" style={{color: 'var(--eventally-dark)'}}>{formattedDate}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2" style={{color: 'var(--eventally-coral)'}}>
                  <path d="m7 7 10 10-10 10"></path>
                </svg>
              </a>
            ) : (
              <div className="inline-flex items-center px-4 py-2 rounded-lg" style={{backgroundColor: 'var(--eventally-gray)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" style={{color: 'var(--eventally-coral)'}}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className="font-semibold" style={{color: 'var(--eventally-dark)'}}>{formattedDate}</span>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4 activity-content" style={{color: 'var(--eventally-dark)'}}>
              <div dangerouslySetInnerHTML={{ __html: activity.text_short }}></div>
            </h2>
          </div>

          {/* Tags */}
          {activity.tags && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3" style={{color: 'var(--eventally-dark)'}}>Categories</h3>
              <div className="flex flex-wrap gap-2">
                {activity.tags.split(',').map(tag => (
                  <span 
                    key={tag.trim()} 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{backgroundColor: 'var(--eventally-light-coral)', color: 'var(--eventally-dark)'}}
                  >
                    {tag.trim().replace('#', '')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-3" style={{color: 'var(--eventally-dark)'}}>About This Event</h3>
            {activity.description ? (
              <div className="text-gray-600 leading-relaxed activity-content">
                <div dangerouslySetInnerHTML={{ __html: activity.description }}></div>
              </div>
            ) : (
              <p className="text-gray-600 leading-relaxed">
                For more information about this event, please follow the link above or contact the event organizers directly. 
                Event details are subject to change, so please verify information before attending.
              </p>
            )}
          </div>

          {/* Activity ID for reference */}
          <div className="mt-6 pt-4 border-t text-sm text-gray-500">
            Activity ID: {activity.id}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ActivityPage;